/**
 * Browser-safe brand runtime contract (TO-367): the artifact shape `virtual:today-brands`
 * serves, the runtime `BrandProvider` exposes, and the shared ordering and labels. Published as
 * the `@today/brand-config/runtime` subpath; imports no `node:` module and no compiler.
 */
import { BrandStatus } from '../brand.types.js'
import type { BrandRuntimeArtifact } from './runtime.types.js'

export {
	type BrandAssetRef,
	type BrandDefinition,
	type BrandIdentity,
	BrandStatus,
	type DesignConfiguration,
} from '../brand.types.js'
export * from './design-catalogue.js'
export * from './runtime.types.js'

/** Status order: published first, invalid artifacts last. */
const STATUS_ORDER: readonly BrandStatus[] = [
	BrandStatus.Published,
	BrandStatus.Deprecated,
	BrandStatus.Approved,
	BrandStatus.ReadyForReview,
	BrandStatus.Draft,
]

const rank = (artifact: BrandRuntimeArtifact): number =>
	artifact.valid ? STATUS_ORDER.indexOf(artifact.status) : STATUS_ORDER.length

/** Orders artifacts by status order (published … draft, then invalid), then by id (UTF-16 code units). */
export function orderArtifacts(artifacts: readonly BrandRuntimeArtifact[]): BrandRuntimeArtifact[] {
	return [...artifacts].sort((a, b) => rank(a) - rank(b) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0))
}

/**
 * The Storybook Brand toolbar title of an artifact: the name of a published brand,
 * `<name> · <status>` for any other valid brand, and `<id> · invalid` for one that failed to compile.
 */
export function brandToolbarTitle(artifact: BrandRuntimeArtifact): string {
	if (!artifact.valid) return `${artifact.id} · invalid`
	return artifact.status === BrandStatus.Published ? artifact.name : `${artifact.name} · ${artifact.status}`
}
export { DEFAULT_PLATFORM_BRAND_ID } from '../platform-default.js'
